import {
  DESTINATION_CHANNEL_FEATURE,
  INTEGRATION_MANIFESTS,
  INTRO_OFFER_CHARGE_CENTS,
  INTRO_OFFER_MAX_EVENT_BUCKET,
} from '@logspot-platform/platform-types';
import { z } from 'zod';
import { formatCurrency } from '@/lib/currency';

export const INTRO_OFFER_PRICE = formatCurrency(INTRO_OFFER_CHARGE_CENTS / 100, 'USD');
export const INTRO_OFFER_SCOPE = `on plans up to ${formatBucketLabel(INTRO_OFFER_MAX_EVENT_BUCKET)} events`;
export const introOfferCovers = (bucket: number): boolean =>
  bucket <= INTRO_OFFER_MAX_EVENT_BUCKET;

export const PLAN_TIERS = ['FREE', 'ESSENTIALS', 'PRO', 'ENTERPRISE'] as const;
export type PlanTier = (typeof PLAN_TIERS)[number];

export const EVENT_BUCKETS = [
  10_000, 100_000, 200_000, 500_000, 1_000_000, 2_000_000, 5_000_000,
  10_000_000,
] as const;
export type EventBucket = (typeof EVENT_BUCKETS)[number];

// A plan picked on the pricing page. Bad values drop to undefined instead of failing the route.
export const pricingLinkSearch = {
  plan: z.enum(['essentials', 'pro']).optional().catch(undefined),
  interval: z.enum(['monthly', 'yearly']).optional().catch(undefined),
  events: z.coerce
    .number()
    .refine((n) => (EVENT_BUCKETS as readonly number[]).includes(n))
    .optional()
    .catch(undefined),
};

// New sign-ups carry the plan into setup; signed-in users go straight to the plan sheet.
export const pricingLinkPath = (
  path: '/onboarding' | '/getting-started',
  search: { plan?: string; interval?: string; events?: number },
): string | undefined =>
  search.plan
    ? `${path}?plan=${search.plan}&interval=${search.interval ?? 'monthly'}${search.events ? `&events=${search.events}` : ''}`
    : undefined;

// Sales-led Enterprise bands. Used only by super-admin tooling — never
// rendered on the public pricing slider.
export const ENTERPRISE_EVENT_BUCKETS = [
  10_000_000, 25_000_000, 50_000_000, 100_000_000,
] as const;
export type EnterpriseEventBucket = (typeof ENTERPRISE_EVENT_BUCKETS)[number];

// Mirrors the backend allowlist. Enterprise stays out because its rates are private.
export const PUBLIC_PRICING_TIERS = ['essentials', 'pro'] as const;
export type PublicPricingTier = (typeof PUBLIC_PRICING_TIERS)[number];

export type PricingCell = {
  priceCents: number;
  addonRate: number;
  overageRate: number;
};

export const PRICING_MATRIX: {
  essentials: Record<number, PricingCell>;
  pro: Record<number, PricingCell>;
  enterprise: Record<number, PricingCell>;
} = {
  essentials: {
    10_000:     { priceCents: 900,   addonRate: 0.001080,    overageRate: 0.00135 },
    100_000:    { priceCents: 1900,  addonRate: 0.000228,    overageRate: 0.000285 },
    200_000:    { priceCents: 2900,  addonRate: 0.000174,    overageRate: 0.0002175 },
    500_000:    { priceCents: 4900,  addonRate: 0.0001176,   overageRate: 0.000147 },
    1_000_000:  { priceCents: 6900,  addonRate: 0.0000828,   overageRate: 0.0001035 },
    2_000_000:  { priceCents: 8900,  addonRate: 0.0000534,   overageRate: 0.00006675 },
    5_000_000:  { priceCents: 12900, addonRate: 0.00003096,  overageRate: 0.0000387 },
    10_000_000: { priceCents: 16900, addonRate: 0.00002028,  overageRate: 0.00002535 },
  },
  pro: {
    10_000:     { priceCents: 1900,  addonRate: 0.002280,    overageRate: 0.00285 },
    100_000:    { priceCents: 3900,  addonRate: 0.000468,    overageRate: 0.000585 },
    200_000:    { priceCents: 5900,  addonRate: 0.000354,    overageRate: 0.0004425 },
    500_000:    { priceCents: 9900,  addonRate: 0.0002376,   overageRate: 0.000297 },
    1_000_000:  { priceCents: 13900, addonRate: 0.0001668,   overageRate: 0.0002085 },
    2_000_000:  { priceCents: 17900, addonRate: 0.0001074,   overageRate: 0.00013425 },
    5_000_000:  { priceCents: 25900, addonRate: 0.00006216,  overageRate: 0.0000777 },
    10_000_000: { priceCents: 33900, addonRate: 0.00004068,  overageRate: 0.00005085 },
  },
  enterprise: {
    10_000_000:  { priceCents: 59_900,  addonRate: 0.000066, overageRate: 0.000075 },
    25_000_000:  { priceCents: 89_900,  addonRate: 0.000040, overageRate: 0.000045 },
    50_000_000:  { priceCents: 139_900, addonRate: 0.000031, overageRate: 0.000035 },
    100_000_000: { priceCents: 219_900, addonRate: 0.000024, overageRate: 0.0000275 },
  },
};

export function getBucketsForTier(
  tier: PlanTier,
): readonly number[] {
  if (tier === 'ENTERPRISE') return ENTERPRISE_EVENT_BUCKETS;
  if (tier === 'FREE') return [0];
  return EVENT_BUCKETS;
}

export const PLAN_LIMITS = {
  FREE: { domains: 1, users: 1, companies: 10, retention: '3 months', events: 5000 },
  ESSENTIALS: { domains: 3, users: 3, companies: 50, retention: '24 months' },
  PRO: { domains: 10, users: 10, companies: 'Unlimited', retention: '36 months' },
  ENTERPRISE: { domains: '10+', users: '10+', companies: 'Unlimited', retention: 'Custom' },
} as const;

// First plan that includes each destination feature (the registry defaults in platform-types).
const FEATURE_PLAN_LABEL: Partial<Record<string, 'Essentials' | 'Pro'>> = {
  alerts: 'Essentials',
  webhooks: 'Pro',
  advancedIntegrations: 'Pro',
};

// The plan a channel is locked behind for this org, or null when it is available.
export function lockedChannelPlan(
  channel: string,
  isEnabled: (feature: string) => boolean,
): 'Essentials' | 'Pro' | null {
  const feature = DESTINATION_CHANNEL_FEATURE[channel];
  return feature && !isEnabled(feature)
    ? (FEATURE_PLAN_LABEL[feature] ?? null)
    : null;
}

// The plan an integration-only destination (Clay) is locked behind, from its
// catalog entry. Plain notify/webhook destinations are gated by channel above;
// the hub's own Slack and webhook integrations share those types.
export function lockedIntegrationPlan(
  destinationType: string,
  isEnabled: (feature: string) => boolean,
): 'Essentials' | 'Pro' | null {
  if (destinationType === 'notify' || destinationType === 'webhook') return null;
  const feature = INTEGRATION_MANIFESTS.find(
    (manifest) => manifest.deliversAs === destinationType,
  )?.requiredFeature;
  return feature && !isEnabled(feature)
    ? (FEATURE_PLAN_LABEL[feature] ?? null)
    : null;
}

// Mirrors the backend pricing matrix and the FEATURES registry plan defaults.
export const PLAN_FEATURES = {
  FREE: { alerts: false, webhooks: false, apiAccess: false, sso: false },
  ESSENTIALS: { alerts: true, webhooks: false, apiAccess: false, sso: false },
  PRO: { alerts: true, webhooks: true, apiAccess: true, sso: false },
  ENTERPRISE: { alerts: true, webhooks: true, apiAccess: true, sso: true },
} as const;

export function formatBucketLabel(bucket: number): string {
  if (bucket >= 1_000_000) {
    return `${bucket / 1_000_000}M`;
  }
  return `${bucket / 1_000}k`;
}

export function getMonthlyPrice(
  tier: 'essentials' | 'pro',
  bucket: number,
): number {
  const entry = PRICING_MATRIX[tier][bucket];
  if (!entry) return 0;
  return entry.priceCents / 100;
}

export function getYearlyPrice(
  tier: 'essentials' | 'pro',
  bucket: number,
): number {
  // 2 months free: charge for 10 months instead of 12
  return getMonthlyPrice(tier, bucket) * 10;
}

export function getYearlyMonthlyEquivalent(
  tier: 'essentials' | 'pro',
  bucket: number,
): number {
  return getYearlyPrice(tier, bucket) / 12;
}

// Bonus Data Credits: mirrors backend DATA_CREDIT_BONUS_RATE + $1=1,000 anchor.
export const DATA_CREDIT_BONUS_RATE = 0.05;
const DATA_CREDIT_EXCHANGE_RATE = 1000;
const YEARLY_BASE_MONTHS = 10;

// The bonus ceiling is the highest price a customer can reach without talking to sales.
export const DATA_CREDIT_BONUS_BASE_CAP = Math.max(
  ...PUBLIC_PRICING_TIERS.flatMap((tier) =>
    Object.values(PRICING_MATRIX[tier]).map((cell) => cell.priceCents / 100),
  ),
);

function bonusCredits(basePrice: number, capPrice: number): number {
  return Math.round(
    Math.min(basePrice, capPrice) * DATA_CREDIT_BONUS_RATE * DATA_CREDIT_EXCHANGE_RATE,
  );
}

export function getMonthlyDataCreditBonus(
  tier: PublicPricingTier,
  bucket: number,
): number {
  return bonusCredits(getMonthlyPrice(tier, bucket), DATA_CREDIT_BONUS_BASE_CAP);
}

export function getYearlyDataCreditBonus(
  tier: PublicPricingTier,
  bucket: number,
): number {
  return bonusCredits(
    getYearlyPrice(tier, bucket),
    DATA_CREDIT_BONUS_BASE_CAP * YEARLY_BASE_MONTHS,
  );
}

// ── Enterprise helpers (super-admin tooling only) ──────────────
// Kept on a separate signature so the public-pricing helpers above
// can never accidentally render an Enterprise band.

export function getEnterpriseMonthlyPrice(bucket: number): number {
  const entry = PRICING_MATRIX.enterprise[bucket];
  if (!entry) return 0;
  return entry.priceCents / 100;
}

export function getEnterpriseAddonRate(bucket: number): number {
  return PRICING_MATRIX.enterprise[bucket]?.addonRate ?? 0;
}

export function getEnterpriseOverageRate(bucket: number): number {
  return PRICING_MATRIX.enterprise[bucket]?.overageRate ?? 0;
}
